/**
 * Auth Store
 * Global state management for authentication
 */

import { create } from 'zustand';
import { User } from '@/types';
import AuthService from '@/services/auth';
import StorageService from '@/services/storage';

export interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isInitialized: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<User>;
  register: (data: any) => Promise<User>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
  clearError: () => void;
  initialize: () => void;
}

const getInitialAuthState = () => {
  const isAuthenticated = StorageService.isAuthenticated();
  const user = StorageService.getUser();

  return {
    user: isAuthenticated ? user : null,
    isAuthenticated,
  };
};

const initialAuthState = getInitialAuthState();

export const useAuthStore = create<AuthState>((set) => ({
  user: initialAuthState.user,
  isLoading: false,
  isAuthenticated: initialAuthState.isAuthenticated,
  isInitialized: true,
  error: null,

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await AuthService.login({ email, password });
      set({
        user: response.user,
        isAuthenticated: true,
        isInitialized: true,
        isLoading: false,
      });
      return response.user;
    } catch (error: any) {
      set({
        error: error.message || 'Login failed',
        isLoading: false,
      });
      throw error;
    }
  },

  register: async (data: any) => {
    set({ isLoading: true, error: null });
    try {
      const response = await AuthService.register(data);
      set({
        user: response.user,
        isAuthenticated: true,
        isInitialized: true,
        isLoading: false,
      });
      return response.user;
    } catch (error: any) {
      set({
        error: error.message || 'Registration failed',
        isLoading: false,
      });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await AuthService.logout();
      set({
        user: null,
        isAuthenticated: false,
        isInitialized: true,
        isLoading: false,
        error: null,
      });
    } catch (error: any) {
      console.error('Logout error:', error);
      // Clear auth data anyway
      AuthService.clearAuth();
      set({
        user: null,
        isAuthenticated: false,
        isInitialized: true,
        isLoading: false,
      });
    }
  },

  setUser: (user: User | null) => {
    set({
      user,
      isAuthenticated: !!user,
      isInitialized: true,
    });
  },

  clearError: () => {
    set({ error: null });
  },

  initialize: () => {
    const isAuthenticated = StorageService.isAuthenticated();
    const user = StorageService.getUser();

    set({
      isAuthenticated,
      user: isAuthenticated ? user : null,
      isInitialized: true,
    });
  },
}));

export default useAuthStore;
